import { isEmpty } from 'ramda';
import { type FC, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router';

import { PATIENT_PROFILE_SCROLL_KEY_PREFIX } from '@/shared/config';
import {
  useAppDispatch,
  useAppSelector,
  useScrollRestoration,
} from '@/shared/hooks';
import { trackEvent } from '@/shared/lib';
import { Layout } from '@/shared/ui';

import { albumModel } from '@/entities/album';
import { usePurchasePrompt } from '@/entities/billing';
import { ModalID, modalModel } from '@/entities/modal';
import { patientModel } from '@/entities/patient';
import { userModel } from '@/entities/user';

import { Footer } from '@/features/footer';

import { AgentChatHost } from '@/widgets/AgentChat';
import { PhotoSlider } from '@/widgets/DentalPhotoSlider';
import { EditAlbumModal } from '@/widgets/EditAlbumModal';
import { Header } from '@/widgets/Header';
import { PatientDocuments } from '@/widgets/PatientDocuments';
import {
  ClinicalCaseDescriptionEditorModal,
  PatientHistoricalData,
  SharedWithInline,
  TreatingDoctorsInline,
} from '@/widgets/PatientInfo';
import { PurchasePrompt } from '@/widgets/PurchasePrompt';
import { StudyCompareModal } from '@/widgets/StudyCompare';

import {
  usePatientProfileItems,
  usePatientProfileVisibility,
  usePatientTimelineRecordings,
} from './hooks';
import { getTimelineItems } from './lib';
import styles from './PatientProfileV2.module.scss';
import { PatientHeaderV2 } from './ui/PatientHeaderV2/PatientHeaderV2';
import { PatientProfileModals } from './ui/PatientProfileModals/PatientProfileModals';
import { PatientSummaryHero } from './ui/PatientSummaryHero/PatientSummaryHero';
import { PatientTimeline } from './ui/PatientTimeline/PatientTimeline';

export const PatientProfileV2: FC = () => {
  const { patientID = '' } = useParams();
  const [agentDocumentsRefreshToken, setAgentDocumentsRefreshToken] =
    useState(0);

  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();

  const user = useAppSelector(userModel.selectors.selectCurrentUser);

  const patient = useAppSelector(
    patientModel.selectors.selectPatientByID(patientID),
  );

  const {
    shouldShow: shouldShowPurchasePrompt,
    isOwner,
    openShop,
  } = usePurchasePrompt();

  const {
    CanEditPatient,
    CanViewStudies,
    dentalPhotoCount,
    enableAgentChatFeature,
    shouldShowPhotosWidget,
    showOrderReport,
  } = usePatientProfileVisibility(patientID);

  const { allItems, hiddenItems, showSkeletons, canViewArchive } =
    usePatientProfileItems(patientID);

  const {
    recordings,
    hiddenRecordings,
    userTimezone,
    hideMode,
    isConsultationStale,
    rowActions,
  } = usePatientTimelineRecordings(patientID);

  const albums = useAppSelector(albumModel.selectors.selectAlbumsWithAssets);

  const reports = allItems.flatMap((item) =>
    item.type === 'report' ? [item.data] : [],
  );
  const timelineItems = getTimelineItems({
    items: allItems,
    recordings,
    albums,
  });
  const hiddenTimelineItems = getTimelineItems({
    items: hiddenItems,
    recordings: hiddenRecordings,
  });

  useScrollRestoration({
    storageKey: `${PATIENT_PROFILE_SCROLL_KEY_PREFIX}${patientID}`,
    isContentLoaded: !showSkeletons,
    maxAttempts: 10,
    attemptInterval: 200,
    timeoutMinutes: 5,
  });

  useEffect(() => {
    trackEvent({
      eventName: 'PatientProfile Page Viewed',
      properties: { patient_id: patientID },
      isPageViewEvent: true,
    });
  }, [patientID]);

  if (isEmpty(user) || patient === undefined) {
    return null;
  }

  const handleEditPatient = () => {
    dispatch(
      modalModel.actions.openModal({
        modalID: ModalID.PatientModalForm,
        data: { patientID },
      }),
    );
  };

  return (
    <>
      <Layout>
        <Layout.Header>
          <Header />
        </Layout.Header>

        <Layout.Content className={styles.desktopContent}>
          <div className={styles.desktopWorkspace}>
            <Layout.Main className={styles.main}>
              <PatientHeaderV2
                patientID={patientID}
                canEditPatient={CanEditPatient}
                showOrderReport={showOrderReport}
                onEditPatient={handleEditPatient}
              />

              <PatientSummaryHero
                patientID={patientID}
                reports={CanViewStudies ? reports : []}
                className={styles.hero}
              />

              <div className={styles.columns}>
                <aside
                  className={styles.sideColumn}
                  data-section="patient-info"
                >
                  <TreatingDoctorsInline patientID={patientID} />

                  <SharedWithInline patientID={patientID} />

                  <PatientHistoricalData patientID={patientID} />

                  {enableAgentChatFeature && (
                    <PatientDocuments
                      key={patientID}
                      patientID={patientID}
                      canEdit={CanEditPatient}
                      refreshToken={agentDocumentsRefreshToken}
                    />
                  )}

                  {shouldShowPurchasePrompt && (
                    <PurchasePrompt
                      title={formatMessage({
                        id: 'orderReport.title',
                        defaultMessage: 'Create AI report',
                      })}
                      isOwner={isOwner}
                      onOpenShop={openShop}
                    />
                  )}
                </aside>

                {CanViewStudies && (
                  <PatientTimeline
                    key={patientID}
                    className={styles.timelineColumn}
                    patientID={patientID}
                    items={timelineItems}
                    hiddenItems={hiddenTimelineItems}
                    showSkeletons={showSkeletons}
                    canViewArchive={canViewArchive}
                    shouldShowPhotosWidget={shouldShowPhotosWidget}
                    dentalPhotoCount={dentalPhotoCount}
                    recordingActions={rowActions}
                    userTimezone={userTimezone}
                    hideMode={hideMode}
                    isConsultationStale={isConsultationStale}
                  />
                )}
              </div>
            </Layout.Main>
          </div>
        </Layout.Content>

        <Layout.Footer>
          <Footer />
        </Layout.Footer>
      </Layout>

      {enableAgentChatFeature && (
        <AgentChatHost
          patientID={patientID}
          onDocumentChanged={() =>
            setAgentDocumentsRefreshToken((current) => current + 1)
          }
        />
      )}

      <ClinicalCaseDescriptionEditorModal />
      <PhotoSlider />
      <EditAlbumModal />
      <StudyCompareModal />
      <PatientProfileModals patientID={patientID} />
    </>
  );
};
